import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type InitialLoadPlan, type InitialLoadResult } from '../api/client';
import { AvisoError } from '../components/Aviso';
import { Palomita } from '../components/Iconos';
import { Pista } from '../components/Pista';
import { SelectorDeLibro } from '../components/SelectorDeLibro';

/**
 * Carga inicial: la plantilla, sus grupos y el catálogo de documentos desde un
 * Excel.
 *
 * **Revisar antes de cargar, siempre.** El primer botón no escribe nada: lee el
 * libro, lo compara con lo que ya hay y dice qué haría. Sólo después aparece el
 * de cargar. Con cientos de personas de por medio, «a ver qué pasa» no es una
 * forma aceptable de enterarse de que la mitad de los correos venían mal.
 *
 * Y cargar dos veces el mismo archivo no duplica nada: si algo falla a mitad,
 * volver a cargarlo completa lo que faltó.
 */
export function CargaInicial() {
  const { t } = useTranslation();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [plan, setPlan] = useState<InitialLoadPlan | null>(null);
  const [resultado, setResultado] = useState<InitialLoadResult | null>(null);
  const [enviar, setEnviar] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function revisar() {
    if (!archivo) return;
    setOcupado(true);
    setError(null);
    setPlan(null);
    setResultado(null);
    try {
      setPlan(await api.previewInitialLoad(archivo));
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  async function cargar() {
    if (!archivo) return;
    setOcupado(true);
    setError(null);
    try {
      setResultado(await api.applyInitialLoad(archivo, enviar));
      setPlan(null);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  const vista = resultado ?? plan;
  const r = vista?.resumen;

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('initialLoad.title')}</h1>
        <p className="pagina__bajada">{t('initialLoad.explanation')}</p>
      </div>

      <AvisoError error={error} />

      <section className="tarjeta">
        <SelectorDeLibro
          archivo={archivo}
          ocupado={ocupado}
          onElegir={(elegido) => {
            setArchivo(elegido);
            setPlan(null);
            setResultado(null);
          }}
        />
        <div className="tarjeta__acciones">
          {/* Un enlace y no un botón: es una descarga, y así el navegador la
              trata como tal. La plantilla la arma el servidor con las mismas
              columnas que exige la carga. */}
          <a className="boton boton--fantasma" href="/api/v1/initial-load/template" download>
            {t('initialLoad.downloadTemplate')}
          </a>
          <button
            className="boton"
            type="button"
            disabled={!archivo || ocupado}
            onClick={() => void revisar()}
          >
            {ocupado && !plan ? t('initialLoad.reviewing') : t('initialLoad.review')}
          </button>
        </div>
      </section>

      {resultado && (
        <p className={`aviso ${resultado.fallos.length ? 'aviso--atencion' : 'aviso--exito'}`}>
          {resultado.fallos.length
            ? t('initialLoad.doneWithFailures', {
                count: resultado.fallos.length,
              })
            : t('initialLoad.done')}
        </p>
      )}

      {r && (
        <section className="tarjeta resumen-carga" aria-label={t('initialLoad.summary')}>
          <h2 className="tarjeta__titulo">{t('initialLoad.summary')}</h2>
          {/* Cinco cifras en una sola tira que se reacomoda. Antes eran cinco
              tarjetas grandes en fila: en una laptop de 13" se estrechaban hasta
              partir los títulos en tres renglones, y el 90 % de las veces las
              cinco dicen cero. Lo que importa es el número; el porqué está en la
              pista. */}
          <ul className="cifras">
            <Cifra
              valor={r.usuariosNuevos}
              etiqueta={t('initialLoad.newUsers')}
              nota={t('initialLoad.existing', { count: r.usuariosExistentes })}
              pista={t('initialLoad.newUsersHint')}
              icono="personas"
            />
            <Cifra
              valor={r.gruposNuevos}
              etiqueta={t('initialLoad.newGroups')}
              nota={t('initialLoad.existing', { count: r.gruposExistentes })}
              pista={t('initialLoad.newGroupsHint')}
              icono="grupos"
            />
            <Cifra
              valor={r.membresiasNuevas}
              etiqueta={t('initialLoad.newMemberships')}
              pista={t('initialLoad.newMembershipsHint')}
              icono="membresias"
            />
            <Cifra
              valor={r.documentosNuevos}
              etiqueta={t('initialLoad.newDocuments')}
              nota={t('initialLoad.existing', { count: r.documentosExistentes })}
              pista={t('initialLoad.newDocumentsHint')}
              icono="documentos"
            />
            <Cifra
              valor={r.documentosPendientes}
              etiqueta={t('initialLoad.pendingDocuments')}
              pista={t('initialLoad.pendingHint')}
              icono="pendientes"
              alerta={r.documentosPendientes > 0}
            />
          </ul>
        </section>
      )}

      {vista && vista.errores.length > 0 && (
        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('initialLoad.errors')}</h2>
            <span className="pildora pildora--critico">
              <span className="pildora__punto" aria-hidden="true" />
              {vista.errores.length}
            </span>
          </div>
          <p className="campo__ayuda">{t('initialLoad.errorsHelp')}</p>
          <Tabla
            columnas={[t('initialLoad.sheet'), t('initialLoad.row'), t('initialLoad.problem')]}
            filas={vista.errores.map((e) => [e.hoja, e.fila ?? '—', e.mensaje])}
          />
        </section>
      )}

      {resultado && resultado.fallos.length > 0 && (
        <section className="tarjeta">
          <h2 className="tarjeta__titulo">{t('initialLoad.failures')}</h2>
          <p className="campo__ayuda">{t('initialLoad.failuresHelp')}</p>
          <Tabla
            columnas={[t('initialLoad.what'), t('initialLoad.problem')]}
            filas={resultado.fallos.map((f) => [f.que, f.detalle])}
          />
        </section>
      )}

      {vista && vista.cambiosDeRol.length > 0 && (
        <section className="tarjeta">
          <h2 className="tarjeta__titulo">{t('initialLoad.roleChanges')}</h2>
          <p className="campo__ayuda">{t('initialLoad.roleChangesHelp')}</p>
          <Tabla
            columnas={[t('users.email'), t('initialLoad.from'), t('initialLoad.to')]}
            filas={vista.cambiosDeRol.map((c) => [c.correo, c.de, c.a])}
          />
        </section>
      )}

      {vista && vista.pendientes.length > 0 && (
        <section className="tarjeta">
          <h2 className="tarjeta__titulo">{t('initialLoad.pendingDocuments')}</h2>
          <p className="campo__ayuda">{t('initialLoad.pendingHelp')}</p>
          <Tabla
            columnas={[t('documents.name'), t('initialLoad.reason')]}
            filas={vista.pendientes.map((p) => [p.titulo, p.motivo])}
          />
        </section>
      )}

      {vista && vista.documentosExistentes.length > 0 && (
        <section className="tarjeta">
          <h2 className="tarjeta__titulo">{t('initialLoad.existingDocuments')}</h2>
          <p className="campo__ayuda">{t('initialLoad.existingDocumentsHelp')}</p>
          <Tabla
            columnas={[t('documents.name'), t('initialLoad.code')]}
            filas={vista.documentosExistentes.map((d) => [d.titulo, d.codigo])}
          />
        </section>
      )}

      {plan && plan.errores.length === 0 && (
        <section className="tarjeta">
          <h2 className="tarjeta__titulo">{t('initialLoad.confirmTitle')}</h2>
          <label className="casilla-ficha">
            <input type="checkbox" checked={enviar} onChange={(e) => setEnviar(e.target.checked)} />
            {/* La marca visible. Sin ella la casilla no se ve y no se sabe si
                está puesta, justo en la opción que decide si salen cientos de
                correos. */}
            <span className="casilla-ficha__marca" aria-hidden="true">
              <Palomita />
            </span>
            <span className="casilla-ficha__nombre">
              {t('initialLoad.sendInvitations', {
                count: plan.resumen.usuariosNuevos,
              })}
            </span>
          </label>
          <p className="campo__ayuda">{t('initialLoad.sendInvitationsHelp')}</p>
          <div className="tarjeta__acciones">
            <button
              className="boton"
              type="button"
              disabled={ocupado}
              onClick={() => void cargar()}
            >
              {ocupado ? t('initialLoad.loading') : t('initialLoad.load')}
            </button>
          </div>
        </section>
      )}
    </>
  );
}

const ICONOS: Record<string, JSX.Element> = {
  personas: (
    <>
      <circle cx="8" cy="6" r="2.6" />
      <path d="M3.5 15c0-2.5 2-4.2 4.5-4.2s4.5 1.7 4.5 4.2" />
    </>
  ),
  grupos: (
    <>
      <circle cx="6" cy="6.5" r="2.2" />
      <circle cx="11.5" cy="7.5" r="1.8" />
      <path d="M2 14.5c0-2.2 1.8-3.6 4-3.6s4 1.4 4 3.6M10.5 14.5c0-1.7 1.2-2.8 3-2.8" />
    </>
  ),
  membresias: (
    <>
      <circle cx="8" cy="8" r="5.6" />
      <path d="m5.6 8.2 1.7 1.7 3.2-3.4" />
    </>
  ),
  documentos: (
    <>
      <path d="M4 2.5h6l3 3v8H4z" />
      <path d="M10 2.5v3h3M6 8.5h5M6 11h3.5" />
    </>
  ),
  pendientes: (
    <>
      <circle cx="8" cy="8" r="5.6" />
      <path d="M8 4.8v3.5l2.2 1.4" />
    </>
  ),
};

/**
 * Una cifra del resumen: el número, su nombre corto y el porqué en la pista.
 *
 * El número es lo que se mira; el resto acompaña. Por eso la etiqueta va en
 * minúscula y pequeña, y la explicación larga —«se darán de alta como
 * invitadas»— no ocupa espacio hasta que alguien pregunta.
 */
function Cifra({
  valor,
  etiqueta,
  nota,
  pista,
  icono,
  alerta = false,
}: {
  valor: number;
  etiqueta: string;
  nota?: string;
  pista: string;
  icono: keyof typeof ICONOS;
  alerta?: boolean;
}) {
  return (
    <li className={`cifra${alerta ? ' cifra--alerta' : ''}${valor === 0 ? ' cifra--cero' : ''}`}>
      <span className="cifra__icono" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          {ICONOS[icono]}
        </svg>
      </span>
      <span className="cifra__valor">{valor}</span>
      <span className="cifra__etiqueta">
        {etiqueta}
        <Pista texto={pista} />
      </span>
      {nota && <span className="cifra__nota">{nota}</span>}
    </li>
  );
}

function Tabla({ columnas, filas }: { columnas: string[]; filas: (string | number)[][] }) {
  return (
    <div className="tabla-envoltura">
      <table>
        <thead>
          <tr>
            {columnas.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={i}>
              {f.map((v, j) => (
                <td key={j}>{v}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
